import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { getMe, loginUser, registerUser, logoutUser } from "@/lib/api";
import { logWarn } from "@/lib/log";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = guest, object = logged in
    const [user, setUser] = useState(null);

    const checkAuth = useCallback(async () => {
        try {
            setUser(await getMe());
        } catch (e) {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(async (email, password) => {
        const u = await loginUser({ email, password });
        setUser(u);
        return u;
    }, []);

    const register = useCallback(async (email, password, display_name, accept_terms = true) => {
        const u = await registerUser({ email, password, display_name, accept_terms });
        setUser(u);
        return u;
    }, []);

    const logout = useCallback(async () => {
        try {
            await logoutUser();
        } catch (e) {
            logWarn("logout failed", e);
        }
        setUser(false);
    }, []);

    const updateCredits = useCallback((credits) => {
        setUser((prev) => (prev && typeof prev === "object" ? { ...prev, credits } : prev));
    }, []);

    const contextValue = useMemo(
        () => ({ user, setUser, checkAuth, login, register, logout, updateCredits }),
        [user, checkAuth, login, register, logout, updateCredits]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
